import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid'
import { AccountType, accountTypes } from 'config/user.type.constants'
import { usePermissions } from 'hooks/usePermissions'
import { resolveFloaterPageKey } from 'pages/Admin/AdminTools/Configuration/FloaterTabs/constants'
import { INote, PINNED_NOTES_CHANGED_EVENT } from 'pages/Loan/Servicing/Notes/types'
import { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getPinnedNotes, updateNotePinned } from 'services'
import { Tooltip } from 'stories/components'
import { formatTime, isLoanLevelPage, stripHtmlTags, timeAgo } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

const COLLAPSED_KEY = 'pinnedNotesCollapsed'

const clampStyle: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

const noteAuthor = (noteBy: string) => {
  const splits = (noteBy || '').split(' - ')
  if (splits.length < 2) return splits[0] || ''
  return `${accountTypes[splits[0]] || splits[0]} - ${splits[1]}`
}

/**
 * Notes pinned from the Notes screen, shown at the top of every loan-level page so
 * important information travels with the loan instead of living one tab away.
 */
export const PinnedNotesBanner = () => {
  const location = useLocation()
  const auth = useSelector((state: any) => state.auth)
  const { hasPermission } = usePermissions()

  const [notes, setNotes] = useState<INote[]>([])
  const [loanNo, setLoanNo] = useState(0)
  const [collapsed, setCollapsed] = useState(localStorage.getItem(COLLAPSED_KEY) === 'true')
  const [expandedIds, setExpandedIds] = useState<number[]>([])

  const { isBorrower, isInvestor, accountType } = auth.profile || {}
  const canView = !isBorrower && !isInvestor && accountType !== AccountType.VENDOR && hasPermission('Loan.Servicing.Notes')
  const canPin = hasPermission('Loan.Servicing.Notes.Pin')

  useEffect(() => {
    // Some loan screens live at a legacy route isLoanLevelPage() doesn't cover (servicing renders at
    // both /servicing/:no and /configurablePage/servicing/:no), so fall back to the floater's resolver.
    const menu = new URLSearchParams(location.search).get('menu')
    const isLoanPage = isLoanLevelPage(location.pathname) || !!resolveFloaterPageKey(location.pathname, menu)
    if (!isLoanPage) {
      setLoanNo(0)
      return
    }
    setLoanNo(Number(setLoanNumber()) || 0)
  }, [location.pathname, location.search])

  const refetch = useCallback(() => {
    if (!loanNo || !canView || !auth.token) {
      setNotes([])
      return
    }
    getPinnedNotes(loanNo)
      .then((data: INote[]) => setNotes(data || []))
      .catch(() => setNotes([]))
  }, [loanNo, canView, auth.token])

  useEffect(() => {
    setExpandedIds([])
    refetch()
  }, [refetch])

  useEffect(() => {
    // Ignore the events this banner raises itself - it already updated its own list.
    const onChanged = (event: Event) => {
      if ((event as CustomEvent)?.detail?.source === 'banner') return
      refetch()
    }
    window.addEventListener(PINNED_NOTES_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(PINNED_NOTES_CHANGED_EVENT, onChanged)
  }, [refetch])

  const onToggleCollapse = () => {
    const value = !collapsed
    setCollapsed(value)
    localStorage.setItem(COLLAPSED_KEY, value ? 'true' : 'false')
  }

  const onUnpin = async (id: number) => {
    setNotes(notes.filter((note) => note.id !== id))
    await updateNotePinned(id, false)
    window.dispatchEvent(
      new CustomEvent(PINNED_NOTES_CHANGED_EVENT, {
        detail: { loanNumber: loanNo, source: 'banner', id, isPinned: false },
      }),
    )
  }

  const onToggleExpand = (id: number) => {
    setExpandedIds(expandedIds.includes(id) ? expandedIds.filter((v) => v !== id) : [...expandedIds, id])
  }

  if (!loanNo || !canView || !notes.length) return <></>

  return (
    <div className="pinned-notes-banner px-2 pt-3">
      <div className="max-w-screen-2xl mx-auto rounded border border-amber-300 bg-amber-50 shadow1">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900">
            <BookmarkSolidIcon className="w-4 h-4" />
            <span>Pinned Notes</span>
            <span className="bg-amber-200 text-amber-900 text-[11px] font-semibold px-2 py-[1px] rounded-full">
              {notes.length}
            </span>
          </div>
          <button
            className="text-amber-900 hover-shadow1 p-1 rounded"
            onClick={onToggleCollapse}
            title={collapsed ? 'Show pinned notes' : 'Hide pinned notes'}
          >
            {collapsed ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />}
          </button>
        </div>
        {!collapsed && (
          <div className="border-t border-amber-200 divide-y divide-amber-200">
            {notes.map((note) => {
              const isExpanded = expandedIds.includes(note.id)
              return (
                <div key={note.id} className="flex items-start gap-3 px-3 py-2 text-[13px] text-gray-900">
                  <div className="flex-1 min-w-0">
                    {!!note.subject && (
                      <div className="font-semibold mb-[2px]">{stripHtmlTags(note.subject)}</div>
                    )}
                    <div
                      className="cursor-pointer"
                      // tailwind 3.1 has no line-clamp utility, so clamp inline.
                      style={isExpanded ? undefined : clampStyle}
                      onClick={() => onToggleExpand(note.id)}
                      dangerouslySetInnerHTML={{
                        __html: isExpanded ? note.content : stripHtmlTags(note.content),
                      }}
                    />
                    <div className="text-[11px] text-gray-600 mt-1">
                      {noteAuthor(note.noteBy)}
                      {' · '}
                      {formatTime(note.createdAt)}
                      {!!timeAgo(note.createdAt) && ` (${timeAgo(note.createdAt)})`}
                    </div>
                  </div>
                  {canPin && (
                    <button className="text-amber-900 hover-shadow1 p-1 rounded shrink-0" onClick={() => onUnpin(note.id)}>
                      <Tooltip message="Unpin">
                        <XMarkIcon className="w-4 h-4" />
                      </Tooltip>
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
